'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
};

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('common');

  // Create schema with translated messages
  const formSchema = z.object({
    firstName: z.string().min(2, t('forms.validation.minLength', { field: t('forms.labels.firstName'), min: 2 })),
    lastName: z.string().min(2, t('forms.validation.minLength', { field: t('forms.labels.lastName'), min: 2 })),
    email: z.string().email(t('forms.validation.emailInvalid')),
    phone: z.string().min(5, t('forms.validation.minLength', { field: t('forms.labels.phone'), min: 5 })),
    message: z.string()
      .min(10, t('forms.validation.minLength', { field: t('forms.labels.message'), min: 10 }))
      .max(500, t('forms.validation.maxLength', { field: t('forms.labels.message'), max: 500 })),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      toast({
        title: t('forms.messages.success.title'),
        description: t('forms.messages.success.contact'),
      });

      reset();
    } catch (error) {
      toast({
        title: t('forms.messages.error.title'),
        description: t('forms.messages.error.general'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" suppressHydrationWarning>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            {...register('firstName')}
            placeholder={t('forms.placeholders.firstName')}
            className="h-12"
            aria-label={t('forms.labels.firstName')}
          />
          {errors.firstName && (
            <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
          )}
        </div>
        <div>
          <Input
            {...register('lastName')}
            placeholder={t('forms.placeholders.lastName')}
            className="h-12"
            aria-label={t('forms.labels.lastName')}
          />
          {errors.lastName && (
            <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      <div>
        <Input
          {...register('email')}
          type="email"
          placeholder={t('forms.placeholders.email')}
          className="h-12"
          aria-label={t('forms.labels.email')}
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
        )}
      </div>

      <div>
        <Input
          {...register('phone')}
          type="tel"
          placeholder={t('forms.placeholders.phone')}
          className="h-12"
          aria-label={t('forms.labels.phone')}
        />
        {errors.phone && (
          <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
        )}
      </div>

      <div>
        <Textarea
          {...register('message')}
          placeholder={t('forms.placeholders.message')}
          rows={5}
          className="resize-none"
          aria-label={t('forms.labels.message')}
        />
        {errors.message && (
          <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
        )}
      </div>

      <Button
        type="submit"
        className="w-full h-12 bg-gold hover:bg-gold-600 text-white font-semibold"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('forms.buttons.sending')}
          </>
        ) : (
          t('forms.buttons.send')
        )}
      </Button>
    </form>
  );
}